import { EntryType, InputMaybe, UsersInput, UserStatus } from "../../generated";
import { PAGE_LIMIT } from "../constants";
import { OutlookError, OutlookUser, RBEvent, SelectRoleFilter } from "../interfaceTypes";
import { AuthCodeMSALBrowserAuthenticationProvider } from '@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser';

export interface State {
  isOpen: boolean;
  isEdit: boolean;
  isCsvUpload: boolean,
  isInitialCsvUpload: boolean,
  isDeleteOpen: boolean;
  isDeleteCaseOpen: boolean;
  isLoading: boolean;
  userRoles: UsersInput["roles"];
  rolesData: SelectRoleFilter[] | undefined;
  searchValue: string | undefined;
  searchDataCount: number;
  totalDataCount: number;
  statusFilter: InputMaybe<UserStatus>;
  currentPage: number;
  dateFilter: string;
  pageLimit: number;
  myCasesCurrentPage: number;
  entityType: string;
  toDateFilter: string;
  totalPageCount: number;
  myCasesCount: number;
  myCasesPageCount: number;
  filterDataCount: number;
  outlookUser?: OutlookUser;
  outlookSignIn?: () => void;
  outlookError?: OutlookError;
  outlookSignOut?: () => void;
  outlookDisplayError?: Function;
  outlookClearError?: Function;
  outlookAuthProvider?: AuthCodeMSALBrowserAuthenticationProvider;
  currentEvent: RBEvent | undefined;
  isTimeEntry?: boolean
  isExpenseEntry?: boolean
  isCostEntry?: boolean
  isLegalFee?: boolean
  isNonClientEntry?: boolean
  entryType: EntryType
}

export const initialState: State = {
  isOpen: false,
  isEdit: false,
  isCsvUpload: false,
  isInitialCsvUpload: false,
  isLoading: false,
  isDeleteOpen: false,
  isDeleteCaseOpen: false,
  userRoles: [],
  rolesData: undefined,
  searchValue: undefined,
  searchDataCount: 0,
  totalDataCount: 0,
  statusFilter: null,
  currentPage: 1,
  myCasesCurrentPage: 1,
  dateFilter: "",
  entityType: "",
  pageLimit: PAGE_LIMIT,
  toDateFilter: "",
  totalPageCount: 1,
  myCasesCount: 0,
  myCasesPageCount: 0,
  filterDataCount: 0,
  outlookUser: undefined,
  outlookSignIn: undefined,
  outlookSignOut: undefined,
  outlookError: undefined,
  outlookDisplayError: undefined,
  outlookClearError: undefined,
  outlookAuthProvider: undefined,
  currentEvent: undefined,
  isTimeEntry: false,
  isExpenseEntry: false,
  isCostEntry: false,
  isLegalFee: false,
  isNonClientEntry: false,
  entryType: EntryType.Time
}

export enum ActionType {
  SET_IS_OPEN = 'setIsOpen',
  SET_IS_DELETE_CASE_OPEN = 'setIsDeleteCaseOpen',
  SET_IS_EDIT = 'setIsEdit',
  SET_IS_CSV_Upload = 'setIsCsvUpload',
  SET_IS_INITIAL_CSV_Upload = 'setIsInitialCsvUpload',
  SET_IS_LOADING = 'setIsLoading',
  SET_USER_ROLES = 'setUserRoles',
  SET_ROLES_DATA = 'setRolesData',
  SET_IS_DELETE_OPEN = 'setIsDeleteOpen',
  SET_SEARCH_VALUE = 'setSearchValue',
  SET_SEARCH_DATA_COUNT = 'setSearchDataCount',
  SET_TOTAL_DATA_COUNT = 'setTotalDataCount',
  SET_STATUS_FILTER = 'setStatusFilter',
  SET_CURRENT_PAGE = 'setCurrentPage',
  SET_MY_CASES_CURRENT_PAGE = 'setMyCasesCurrentPage',
  SET_DATE_FILTER = 'setDateFilter',
  SET_PAGE_LIMIT = "setPageLimit",
  SET_ENTITY_TYPE = 'setEntityType',
  SET_TO_DATE_FILTER = 'setToDateFilter',
  SET_TOTAL_PAGE_COUNT = "setTotalPageCount",
  SET_MY_CASES_PAGE_COUNT = "setMyCasesPageCount",
  SET_MY_CASES_COUNT = "setMyCasesCount",
  SET_FILTER_DATA_COUNT = 'setFilterDataCount',
  SET_OUTLOOK_USER = 'setOutlookUser',
  SET_OUTLOOK_SIGN_IN = 'setOutlookSignIn',
  SET_OUTLOOK_SIGN_OUT = 'setOutlookSignOut',
  SET_OUTLOOK_ERROR = 'setOutlookError',
  SET_OUTLOOK_DISPLAY_ERROR = 'setOutlookDisplayError',
  SET_OUTLOOK_CLEAR_ERROR = 'setOutlookClearError',
  SET_OUTLOOK_AUTH_PROVIDER = 'setOutlookAuthProvider',
  SET_CURRENT_EVENT = 'setCurrentEvent',
  SET_IS_EXPENSE_ENTRY = 'setIsExpenseEntry',
  SET_TIME_ENTRY = "setTimeEntry",
  SET_COST_ENTRY = "setCostEntry",
  SET_LEGAL_FEE = "setLegalFee",
  SET_NON_CLIENT_ENTRY = "setNonClientEntry",
  SET_ENTRY_TYPE = "setEntryType"
}

export type Action =
  | { type: ActionType.SET_IS_OPEN, isOpen: boolean }
  | { type: ActionType.SET_IS_EDIT, isEdit: boolean }
  | { type: ActionType.SET_IS_CSV_Upload, isCsvUpload: boolean }
  | { type: ActionType.SET_IS_INITIAL_CSV_Upload, isInitialCsvUpload: boolean }
  | { type: ActionType.SET_IS_DELETE_OPEN, isDeleteOpen: boolean }
  | { type: ActionType.SET_IS_DELETE_CASE_OPEN, isDeleteCaseOpen: boolean }
  | { type: ActionType.SET_IS_LOADING, isLoading: boolean }
  | { type: ActionType.SET_USER_ROLES, userRoles: UsersInput["roles"] }
  | { type: ActionType.SET_ROLES_DATA, rolesData: SelectRoleFilter[] | undefined }
  | { type: ActionType.SET_SEARCH_VALUE, searchValue?: string }
  | { type: ActionType.SET_SEARCH_DATA_COUNT, searchDataCount: number }
  | { type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: number }
  | { type: ActionType.SET_STATUS_FILTER, statusFilter: InputMaybe<UserStatus> }
  | { type: ActionType.SET_CURRENT_PAGE, currentPage: number }
  | { type: ActionType.SET_DATE_FILTER, dateFilter: string }
  | { type: ActionType.SET_PAGE_LIMIT, pageLimit: number }
  | { type: ActionType.SET_ENTITY_TYPE, entityType: string }
  | { type: ActionType.SET_TO_DATE_FILTER, toDateFilter: string }
  | { type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: number }
  | { type: ActionType.SET_MY_CASES_COUNT, myCasesCount: number }
  | { type: ActionType.SET_MY_CASES_PAGE_COUNT, myCasesPageCount: number }
  | { type: ActionType.SET_MY_CASES_CURRENT_PAGE, myCasesCurrentPage: number }
  | { type: ActionType.SET_FILTER_DATA_COUNT, filterDataCount: number }
  | { type: ActionType.SET_OUTLOOK_USER, outlookUser: OutlookUser }
  | { type: ActionType.SET_OUTLOOK_SIGN_IN, outlookSignIn: () => void }
  | { type: ActionType.SET_OUTLOOK_SIGN_OUT, outlookSignOut: () => void }
  | { type: ActionType.SET_OUTLOOK_ERROR, outlookError: OutlookError }
  | { type: ActionType.SET_OUTLOOK_DISPLAY_ERROR, outlookDisplayError: Function }
  | { type: ActionType.SET_OUTLOOK_CLEAR_ERROR, outlookClearError: Function }
  | { type: ActionType.SET_OUTLOOK_AUTH_PROVIDER, outlookAuthProvider: AuthCodeMSALBrowserAuthenticationProvider }
  | { type: ActionType.SET_CURRENT_EVENT, currentEvent: RBEvent | undefined }
  | { type: ActionType.SET_IS_EXPENSE_ENTRY; value: boolean }
  | { type: ActionType.SET_TIME_ENTRY; value: boolean }
  | { type: ActionType.SET_COST_ENTRY; value: boolean }
  | { type: ActionType.SET_LEGAL_FEE; value: boolean }
  | { type: ActionType.SET_NON_CLIENT_ENTRY; value: boolean }
  | { type: ActionType.SET_ENTRY_TYPE; entryType: EntryType }

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionType.SET_IS_LOADING:
      return {
        ...state,
        isLoading: action.isLoading
      }

    case ActionType.SET_IS_OPEN:
      return {
        ...state,
        isOpen: action.isOpen
      }

    case ActionType.SET_IS_DELETE_OPEN:
      return {
        ...state,
        isDeleteOpen: action.isDeleteOpen
      }

    case ActionType.SET_IS_DELETE_CASE_OPEN:
      return {
        ...state,
        isDeleteCaseOpen: action.isDeleteCaseOpen
      }

    case ActionType.SET_IS_CSV_Upload:
      return {
        ...state,
        isCsvUpload: action.isCsvUpload
      }

    case ActionType.SET_IS_INITIAL_CSV_Upload:
      return {
        ...state,
        isInitialCsvUpload: action.isInitialCsvUpload
      }

    case ActionType.SET_SEARCH_VALUE:
      return {
        ...state,
        searchValue: action.searchValue
      }

    case ActionType.SET_SEARCH_DATA_COUNT:
      return {
        ...state,
        searchDataCount: action.searchDataCount
      }

    case ActionType.SET_IS_EDIT:
      return {
        ...state,
        isEdit: action.isEdit
      }
    case ActionType.SET_USER_ROLES:
      return {
        ...state,
        userRoles: action.userRoles
      }

    case ActionType.SET_TOTAL_DATA_COUNT:
      return {
        ...state,
        totalDataCount: action.totalDataCount
      }

    case ActionType.SET_STATUS_FILTER:
      return {
        ...state,
        statusFilter: action.statusFilter
      }

    case ActionType.SET_ROLES_DATA:
      return {
        ...state,
        rolesData: action.rolesData
      }

    case ActionType.SET_CURRENT_PAGE:
      return {
        ...state,
        currentPage: action.currentPage
      }

    case ActionType.SET_DATE_FILTER:
      return {
        ...state,
        dateFilter: action.dateFilter
      }

    case ActionType.SET_PAGE_LIMIT:
      return {
        ...state,
        pageLimit: action.pageLimit
      }

    case ActionType.SET_ENTITY_TYPE:
      return {
        ...state,
        entityType: action.entityType
      }

    case ActionType.SET_TO_DATE_FILTER:
      return {
        ...state,
        toDateFilter: action.toDateFilter
      }

    case ActionType.SET_TOTAL_PAGE_COUNT:
      return {
        ...state,
        totalPageCount: action.totalPageCount
      }

    case ActionType.SET_MY_CASES_COUNT:
      return {
        ...state,
        myCasesCount: action.myCasesCount
      }

    case ActionType.SET_MY_CASES_PAGE_COUNT:
      return {
        ...state,
        myCasesPageCount: action.myCasesPageCount
      }

    case ActionType.SET_MY_CASES_CURRENT_PAGE:
      return {
        ...state,
        myCasesCurrentPage: action.myCasesCurrentPage
      }

    case ActionType.SET_FILTER_DATA_COUNT:
      return {
        ...state,
        filterDataCount: action.filterDataCount
      }

    case ActionType.SET_OUTLOOK_USER:
      return {
        ...state,
        outlookUser: action.outlookUser
      }

    case ActionType.SET_OUTLOOK_SIGN_IN:
      return {
        ...state,
        outlookSignIn: action.outlookSignIn
      }

    case ActionType.SET_OUTLOOK_SIGN_OUT:
      return {
        ...state,
        outlookSignOut: action.outlookSignOut
      }

    case ActionType.SET_OUTLOOK_ERROR:
      return {
        ...state,
        outlookError: action.outlookError
      }

    case ActionType.SET_OUTLOOK_DISPLAY_ERROR:
      return {
        ...state,
        outlookDisplayError: action.outlookDisplayError
      }

    case ActionType.SET_OUTLOOK_CLEAR_ERROR:
      return {
        ...state,
        outlookClearError: action.outlookClearError
      }

    case ActionType.SET_OUTLOOK_AUTH_PROVIDER:
      return {
        ...state,
        outlookAuthProvider: action.outlookAuthProvider
      }

    case ActionType.SET_CURRENT_EVENT:
      return {
        ...state,
        currentEvent: action.currentEvent
      }
    case ActionType.SET_IS_EXPENSE_ENTRY:
      return {
        ...state,
        isExpenseEntry: action.value
      }
      case ActionType.SET_TIME_ENTRY:
        return {
          ...state,
          isTimeEntry: action.value
        }
      case ActionType.SET_COST_ENTRY:
        return {
        ...state,
        isCostEntry: action.value
      }
      case ActionType.SET_LEGAL_FEE:
        return {
          ...state,
          isLegalFee: action.value
        }
      case ActionType.SET_NON_CLIENT_ENTRY:
        return {
          ...state,
          isNonClientEntry: action.value
        }
      case ActionType.SET_ENTRY_TYPE:
        return {
        ...state,
        entryType: action.entryType
      }
  }
}
